import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Video, AudioLines, Briefcase, ChevronDown, ArrowRight } from "lucide-react";
import MisteckMark from "@/components/brand/MisteckMark";
import { HUBS } from "@/constants/hubs";

// Guided Entrance gate — shown once per session after login. Keeps a new creator
// calm: one pulsing brand mark + three clear workflows, with the full 11-hub
// directory tucked behind a quiet "Explore all" dropdown for advanced users.
const CORE = [
  { key: "video_hub", label: "Create Video", sub: "Reels & video suites", Icon: Video },
  { key: "music_hub", label: "Edit Audio", sub: "Music Studio & Audio Lab", Icon: AudioLines },
  { key: "business_hub", label: "Manage Business", sub: "Billing, ledger & account", Icon: Briefcase },
];

export default function GuidedEntrance({ onOpenHub, onExplore }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="fixed inset-0 z-[60] flex flex-col items-center overflow-y-auto bg-[#0A0A0A] px-6 py-10"
      style={{ fontFamily: "'Outfit', sans-serif" }}
      data-testid="guided-entrance"
    >
      {/* Brand moment */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}
        className="mt-6 flex flex-col items-center"
      >
        <MisteckMark size={72} pulse glow />
        <span className="mt-4 text-2xl font-semibold lowercase tracking-tight text-white">misteck</span>
        <p className="mt-1 text-[13px] text-white/45">What do you want to make today?</p>
      </motion.div>

      {/* Three core workflows */}
      <div className="mt-10 flex w-full max-w-[420px] flex-col gap-3.5">
        {CORE.map((c, i) => (
          <motion.button
            key={c.key}
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.12 + i * 0.08, ease: "easeOut" }}
            onClick={() => onOpenHub(c.key)}
            data-testid={`gate-${c.key}`}
            className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-[#141416] p-5 text-left transition-colors duration-200 hover:border-[#FFD028]/50 hover:bg-[#17171a]"
          >
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-white/10 text-white/80 transition-colors duration-200 group-hover:border-[#FFD028]/50 group-hover:text-[#FFD028]">
              <c.Icon size={22} strokeWidth={1.6} />
            </span>
            <span className="flex-1">
              <span className="block text-base font-semibold text-white">{c.label}</span>
              <span className="block text-[12px] text-white/45">{c.sub}</span>
            </span>
            <ArrowRight size={18} className="text-white/25 transition-all duration-200 group-hover:translate-x-0.5 group-hover:text-[#FFD028]" />
          </motion.button>
        ))}
      </div>

      <div className="flex-1" />

      {/* Hidden compass — full 11-hub directory for advanced users */}
      <div className="mt-10 w-full max-w-[420px]">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="gate-explore-toggle"
          className="mx-auto flex items-center gap-1.5 py-2 text-[12px] font-medium tracking-wide text-white/40 transition-colors hover:text-white/80"
        >
          Explore All Creative Studios
          <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="overflow-hidden"
              data-testid="gate-directory"
            >
              <div className="mt-3 grid grid-cols-2 gap-2 pb-4">
                {HUBS.map((h) => {
                  const Ico = Icons[h.icon] || Icons.LayoutGrid;
                  return (
                    <button
                      key={h.key}
                      onClick={() => onOpenHub(h.key)}
                      data-testid={`gate-hub-${h.key}`}
                      className="flex items-center gap-2.5 rounded-xl border border-white/8 bg-white/[0.02] px-3 py-2.5 text-left transition-colors duration-200 hover:border-white/20 hover:bg-white/[0.05]"
                    >
                      <Ico size={15} strokeWidth={1.6} className="shrink-0 text-white/55" />
                      <span className="min-w-0">
                        <span className="block truncate text-[12px] font-medium text-white/85">{h.label}</span>
                        <span className="block truncate text-[10px] text-white/35">{h.sub}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
